import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import api from '../services/api';
import useAuthStore from '../store/useAuthStore';

const INITIAL_FORM = {
  clinicName: '',
  doctorName: '',
  email: '',
  mobileNumber: '',
  password: '',
  confirmPassword: '',
};

const MOBILE_PATTERN = /^[0-9+\-\s()]{8,20}$/;
const FREE_TRIAL_UNAVAILABLE_MESSAGE =
  'Free trial signup is temporarily unavailable. Please contact Dektal Support.';

function getFreeTrialError(error) {
  const backendMessage = error?.response?.data?.error;
  if (backendMessage) return backendMessage;

  const status = error?.response?.status;
  if (status === 403) return FREE_TRIAL_UNAVAILABLE_MESSAGE;
  if (status === 429) return 'Too many attempts. Please try again shortly.';
  if (status === 400) return 'Please check the highlighted fields and try again.';
  return 'Unable to start your free trial right now.';
}

function normalizeForm(form) {
  return {
    clinicName: form.clinicName.trim(),
    doctorName: form.doctorName.trim(),
    email: form.email.trim().toLowerCase(),
    mobileNumber: form.mobileNumber.trim(),
    password: form.password,
    confirmPassword: form.confirmPassword,
  };
}

function validateForm(form) {
  const nextErrors = {};

  if (!form.clinicName) nextErrors.clinicName = 'Clinic name is required.';
  if (!form.doctorName) nextErrors.doctorName = 'Doctor name is required.';
  if (!form.email) {
    nextErrors.email = 'Email is required.';
  } else if (!form.email.includes('@')) {
    nextErrors.email = 'Enter a valid email address.';
  }

  if (!form.mobileNumber) {
    nextErrors.mobileNumber = 'Mobile number is required.';
  } else if (!MOBILE_PATTERN.test(form.mobileNumber)) {
    nextErrors.mobileNumber = 'Enter a valid mobile number.';
  }

  if (!form.password) {
    nextErrors.password = 'Password is required.';
  } else if (form.password.length < 8) {
    nextErrors.password = 'Password must be at least 8 characters.';
  }

  if (!form.confirmPassword) {
    nextErrors.confirmPassword = 'Confirm your password.';
  } else if (form.password !== form.confirmPassword) {
    nextErrors.confirmPassword = 'Password and confirm password must match.';
  }

  return nextErrors;
}

function Field({ label, name, type = 'text', value, onChange, placeholder, autoComplete, error }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-slate-700">{label}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${name}-error` : undefined}
        className={`w-full rounded-xl border px-4 py-3 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:ring-4 ${
          error
            ? 'border-rose-300 bg-rose-50 focus:border-rose-500 focus:ring-rose-100'
            : 'border-slate-300 bg-white focus:border-indigo-500 focus:ring-indigo-100'
        }`}
        required
      />
      {error ? (
        <span id={`${name}-error`} className="mt-1.5 block text-sm font-medium text-rose-600">
          {error}
        </span>
      ) : null}
    </label>
  );
}

export default function FreeTrial() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);

  const redirectAfterLogin = (user) => {
    navigate(user?.setupCompleted ? '/dashboard' : '/setup', { replace: true });
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
    setServerError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const normalized = normalizeForm(form);
    const validationErrors = validateForm(normalized);
    setErrors(validationErrors);
    setServerError('');

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      await api.post('/tenants/start-free-trial', {
        clinicName: normalized.clinicName,
        doctorName: normalized.doctorName,
        email: normalized.email,
        mobileNumber: normalized.mobileNumber,
        password: normalized.password,
      });

      const { data } = await api.post('/auth/login', {
        email: normalized.email,
        password: normalized.password,
      });

      login(data.token, data.user);
      redirectAfterLogin(data.user);
    } catch (error) {
      setServerError(getFreeTrialError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-100 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-6xl items-center">
        <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <section className="flex flex-col justify-between rounded-2xl bg-slate-950 p-7 text-white shadow-xl sm:p-8">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-400/15 px-3 py-1 text-sm font-semibold text-emerald-200">
                <ShieldCheck className="h-4 w-4" />
                7 days free. No credit card needed.
              </div>
              <h1 className="mt-6 text-3xl font-bold leading-tight sm:text-4xl">
                Start your 7-day free trial
              </h1>
              <p className="mt-4 max-w-xl text-base leading-7 text-slate-300">
                Create a private clinic workspace for your team, pricing, patients, inventory, invoices, and daily operations.
              </p>
            </div>

            <div className="mt-8 space-y-3 text-sm text-slate-200">
              {[
                'Private clinic account with owner login',
                'Setup guided from the first screen',
                'Trial access handled by the Dektal subscription gate',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-2xl bg-white p-5 shadow-xl sm:p-8">
            <div className="mb-6">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-indigo-600">Dektal 360 Pro</p>
              <h2 className="mt-2 text-2xl font-bold text-slate-950 sm:text-3xl">Create your clinic workspace</h2>
              <p className="mt-2 text-sm text-slate-600">All fields are required to start the free trial.</p>
            </div>

            {serverError ? (
              <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                {serverError}
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field
                  label="Clinic Name"
                  name="clinicName"
                  value={form.clinicName}
                  onChange={updateField}
                  placeholder="Smile Craft Dental"
                  autoComplete="organization"
                  error={errors.clinicName}
                />
                <Field
                  label="Doctor Name"
                  name="doctorName"
                  value={form.doctorName}
                  onChange={updateField}
                  placeholder="Dr. Priya Shah"
                  autoComplete="name"
                  error={errors.doctorName}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={updateField}
                  placeholder="doctor@clinic.com"
                  autoComplete="email"
                  error={errors.email}
                />
                <Field
                  label="Mobile Number"
                  name="mobileNumber"
                  type="tel"
                  value={form.mobileNumber}
                  onChange={updateField}
                  placeholder="+91 98765 43210"
                  autoComplete="tel"
                  error={errors.mobileNumber}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field
                  label="Password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={updateField}
                  placeholder="Minimum 8 characters"
                  autoComplete="new-password"
                  error={errors.password}
                />
                <Field
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={form.confirmPassword}
                  onChange={updateField}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                  error={errors.confirmPassword}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />}
                {loading ? 'Creating workspace...' : 'Start free trial'}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold text-indigo-700 hover:text-indigo-800">
                Log in
              </Link>
            </p>
          </section>
        </div>
      </div>
    </main>
  );
}
